import { createSlice, PayloadAction, SerializedError, WithSlice } from "@reduxjs/toolkit";
import { getDrawRunHistory, getDrawRunWinners, getSmarticoRaffles } from "./thunkActions";
import {
  SmarticoRaffle,
  SmarticoRaffleDraw,
  SmarticoRaffleWinner,
  TRaffleDrawRun,
} from "@/app/redux/features/raffle/interfaces/SmarticoRaffle";
import { rootReducer } from "../../../store";

interface IRaffleSmarticoInitialState {
  raffleId: number;
  raffle: SmarticoRaffle;
  isRaffleLoading: boolean;
  errors: Record<string, SerializedError>;
  additionalData: Record<
    number,
    {
      badge: string;
      requiredTickets: number;
      title: string;
      description: string;
      image: string;
      displayOrder?: number;
      prizesText?: string;
      winnersLabel?: string;
      winnersText?: string;
      ticketsText?: string;
    }
  >;
  drawHistory: TRaffleDrawRun[];
  drawRunWinners: Record<number, SmarticoRaffleWinner[]>;
  loadingWinners: Record<number, boolean>;
  runPagination: Record<number, Pick<SmarticoRaffleDraw, "winners_total" | "winners_limit" | "winners_offset">>;
  loadingHistory: Record<string, boolean>;
  isDrawHistoryLoading: boolean;
}

const INITIAL_STATE: IRaffleSmarticoInitialState = {
  raffleId: null,
  raffle: {
    current_tickets_count: 0,
    custom_data: "",
    custom_section_id: 0,
    description: "",
    draws: [],
    end_date: 0,
    id: 0,
    image_url: "",
    image_url_mobile: "",
    max_tickets_count: 0,
    name: "",
    start_date: 0,
  },
  isRaffleLoading: false,
  errors: {},
  additionalData: {},
  drawRunWinners: {},
  loadingWinners: {},
  runPagination: {},
  loadingHistory: {},
  drawHistory: [],
  isDrawHistoryLoading: true,
};

export const smarticoRaffleSlice = createSlice({
  name: "smarticoRaffle",
  initialState: INITIAL_STATE,
  reducers: {
    setRaffleId: (state, action: PayloadAction<string>) => {
      if (!isNaN(Number(action.payload))) {
        state.raffleId = Number(action.payload);
      }
    },
    setRaffles: (state, action: PayloadAction<SmarticoRaffle[]>) => {
      const foundRaffle = action.payload.find(raffle => raffle.id === state.raffleId);
      if (foundRaffle) {
        state.raffle = foundRaffle;
      }
    },
    setDrawAdditionalData: (
      state,
      action: PayloadAction<
        Record<
          number,
          {
            badge: string;
            requiredTickets: number;
            title: string;
            description: string;
            image: string;
            displayOrder?: number;
            prizesText?: string;
            winnersLabel?: string;
            winnersText?: string;
            ticketsText?: string;
          }
        >
      >
    ) => {
      state.additionalData = action.payload;
    },
  },
  extraReducers: builder =>
    builder
      .addCase(getSmarticoRaffles.pending, state => {
        state.isRaffleLoading = true;
        delete state.errors["getSmarticoRaffles"];
      })
      .addCase(getSmarticoRaffles.fulfilled, (state, action) => {
        const foundRaffle = action.payload.find(raffle => raffle.id === state.raffleId);
        if (foundRaffle) {
          state.raffle = foundRaffle;
        }
        state.isRaffleLoading = false;
      })
      .addCase(getSmarticoRaffles.rejected, (state, action) => {
        state.errors["getSmarticoRaffles"] = action.error;
        state.isRaffleLoading = false;
      })
      .addCase(getDrawRunHistory.pending, (state, action) => {
        if (typeof action.meta.arg.draw_ids !== "undefined") {
          state.loadingHistory[JSON.stringify(action.meta.arg.draw_ids)] = true;
        }
        state.isDrawHistoryLoading = true;
      })
      .addCase(getDrawRunHistory.fulfilled, (state, action) => {
        state.isDrawHistoryLoading = false;
        const flatPayload = [...action.payload.flat()]
          .sort((a, b) => a.execution_ts - b.execution_ts)
          .reduce<TRaffleDrawRun[]>((items, item) => {
            const itemAlreadySet = items.findIndex(draw => draw.run_id === item.run_id) !== -1;
            if (itemAlreadySet) {
              return items;
            }
            return [...items, item];
          }, []);
        state.drawHistory = flatPayload;
        state.drawRunWinners = flatPayload.reduce<Record<number, SmarticoRaffleWinner[]>>((winners, item) => {
          return {
            ...winners,
            [item.run_id]: [],
          };
        }, {});
      })
      .addCase(getDrawRunHistory.rejected, (state, action) => {
        if (typeof action.meta.arg.draw_ids !== "undefined") {
          state.loadingHistory[JSON.stringify(action.meta.arg.draw_ids)] = false;
        }
        state.isDrawHistoryLoading = false;
        if (action.meta.arg.draw_ids) {
          state.errors[`getDrawRunHistory_${JSON.stringify(action.meta.arg.draw_ids) || ""}`] = action.error;
        }
      })
      .addCase(getDrawRunWinners.pending, (state, action) => {
        state.loadingWinners[action.meta.arg.run_id] = true;
      })
      .addCase(getDrawRunWinners.fulfilled, (state, action) => {
        state.loadingWinners[action.meta.arg.run_id] = false;
        const newWinners = action.payload.prizes.flatMap(prize =>
          prize.winners.flatMap(winner => {
            const { winners, ...prizeWithoutWinners } = prize;
            return {
              ...winner,
              prize: prizeWithoutWinners,
            };
          })
        );
        const existing = action.meta.arg.append ? (state.drawRunWinners[action.meta.arg.run_id] || []) : [];
        state.drawRunWinners[action.meta.arg.run_id] = [...existing, ...newWinners];
        state.runPagination[action.meta.arg.run_id] = {
          winners_total: action.payload.winners_total,
          winners_limit: action.payload.winners_limit,
          winners_offset: action.payload.winners_offset,
        };
      })
      .addCase(getDrawRunWinners.rejected, (state, action) => {
        state.loadingWinners[action.meta.arg.run_id] = false;
        state.errors[`getDrawRunWinners_${action.meta.arg.run_id}`] = action.error;
      }),
});

export const { setRaffleId, setDrawAdditionalData, setRaffles } = smarticoRaffleSlice.actions;

declare module "@/app/redux/store" {
  export interface LazyLoadedSlices extends WithSlice<typeof smarticoRaffleSlice> { }
}

smarticoRaffleSlice.injectInto(rootReducer);
